import { TrackEvent, useTracking } from '../../apps/professional-app/fa/common/tracking'
import { useAppSelector } from '../../store/hooks'
import { selectAccountId, selectSafeAuthenticatedUser } from '../../store/auth/authSlice'
import { RoomSessionKind, useStartSessionMutation } from '../../store/media/media'
import React, { useEffect, useMemo, useState } from 'react'
import { parseMetadata } from '../../common/util'
import { getRoomTrackingDetails } from '../../apps/professional-app/fa/tracking'
import { SUPPORTED_MEETING_PROVIDERS } from '../../common/meetingProviders'
import { Button, ButtonVariant } from '../../components/Button/Button'
import { RoomSession } from '../../models/Room'
import { Toast, ToastContainer } from 'react-bootstrap'
import { useSessionAvailability } from '../../components/ScopedBlock/hooks'
import { MissingScopeAction } from '../../components/ScopedBlock/models'
import { ScopedBlock } from '../../components/ScopedBlock/ScopedBlock'

export interface AddAIAssistantButtonProps {
  session: RoomSession
  variant: ButtonVariant
}

export const AddAIAssistantButton: React.FC<AddAIAssistantButtonProps> = ({ session, variant }) => {
  const { tracked } = useTracking()
  const accountId = useAppSelector(selectAccountId)
  const { id: currentUserId } = useAppSelector(selectSafeAuthenticatedUser)
  const [startSession, { isLoading: startingSession, isSuccess: sessionStarted }] = useStartSessionMutation()
  const { source } = useMemo(() => parseMetadata(session.metadata), [session])
  const [showToast, setShowToast] = useState(false)
  const [aiAssistantAdded, setAIAssistantAdded] = useState<boolean>(false)
  const { available: sessionAvailable, isSuccess: sessionAvailabilitySuccess } = useSessionAvailability()

  const atRoomSessionLimitActions = () => {
    return sessionAvailabilitySuccess && sessionAvailable
  }

  useEffect(() => {
    if (sessionStarted) {
      setShowToast(true)
      setAIAssistantAdded(true)
    }
  }, [sessionStarted])

  const _handleOnAddAIAssistant = async (roomId: string, sessionId: string) => {
    try {
      await startSession({
        accountId,
        body: {
          kind: RoomSessionKind.VIRTUAL_PARTICIPANT,
          roomId,
          sessionId,
        },
      }).unwrap()
    } catch (e: any) {
      console.error(e)
    }
  }

  const handleOnAddAIAssistant = tracked(_handleOnAddAIAssistant, { eventName: TrackEvent.AI_ASSISTANT_ADDED, eventDetails: getRoomTrackingDetails(session) })

  if (
    session?.roomCreatedBy?.id !== currentUserId ||
    !SUPPORTED_MEETING_PROVIDERS.includes(source) ||
    session.isActive ||
    new Date(session.startedAt).toDateString() !== new Date().toDateString() ||
    !session.finishedAt ||
    session.finishedAt <= new Date().toISOString()
  ) {
    return null
  }

  return (
    <>
      <ToastContainer position='top-center' className='p-3 summary-toast'>
        <Toast onClose={() => setShowToast(false)} show={showToast} delay={3000} autohide>
          <Toast.Body>Starting AI Assistant... This may take a few seconds.</Toast.Body>
        </Toast>
      </ToastContainer>
      <ScopedBlock
        scope={{
          scope: 'room_session:start',
          superScope: ['room_session:admin', 'room:admin'],
        }}
        missingScopeAction={MissingScopeAction.DISABLE}
      >
        <ScopedBlock authActions={[atRoomSessionLimitActions]} missingScopeAction={MissingScopeAction.DISABLE}>
          <Button
            variant={variant}
            className='button-small text-nowrap'
            loading={startingSession}
            onClick={() => handleOnAddAIAssistant(session.roomId, session.id)}
            disabled={aiAssistantAdded}
          >
            Add AI Assistant
          </Button>
        </ScopedBlock>
      </ScopedBlock>
    </>
  )
}
